import { useCallback, useEffect, useState } from "react";
import { request } from "../../../utils/api";
import { buildLibraryFillStatusMap, buildLibraryRecordIdMap } from "../formLibraryFillStatus";

const LIST_ENDPOINTS = [
  "/application-forms-list",
  "/medical-forms-list",
  "/tax-and-payroll-list",
];

/**
 * Loads filled/pending status for Application Form Library cards
 * for a given employee (reuses Documents list APIs).
 *
 * @param {{
 *   employeeId?: number|string|null,
 *   tenantId?: number|string|null,
 *   enabled?: boolean,
 * }} options
 */
export function useFormLibraryFillStatus({
  employeeId,
  tenantId,
  enabled = true,
} = {}) {
  const [statusByFormId, setStatusByFormId] = useState({});
  const [recordIdByFormId, setRecordIdByFormId] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!enabled || !employeeId || !tenantId) {
      setStatusByFormId({});
      setRecordIdByFormId({});
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const responses = await Promise.all(
        LIST_ENDPOINTS.map((path) =>
          request(
            `${path}?tenantId=${tenantId}&employeeId=${employeeId}`,
          ).catch((err) => {
            console.error(`Fill status fetch failed: ${path}`, err);
            return null;
          }),
        ),
      );

      const rows = [];
      responses.forEach((response) => {
        if (response?.status === 1 && Array.isArray(response.result?.forms)) {
          rows.push(...response.result.forms);
        }
      });

      setStatusByFormId(buildLibraryFillStatusMap(rows));
      setRecordIdByFormId(buildLibraryRecordIdMap(rows));
    } catch (err) {
      console.error("Failed to load form library fill status", err);
      setError(err);
      setStatusByFormId({});
      setRecordIdByFormId({});
    } finally {
      setLoading(false);
    }
  }, [employeeId, tenantId, enabled]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const getStatus = useCallback(
    (formId) => statusByFormId[formId] || null,
    [statusByFormId],
  );

  const isFilled = useCallback(
    (formId) => statusByFormId[formId] === "filled",
    [statusByFormId],
  );

  const getRecordId = useCallback(
    (formId) => recordIdByFormId[formId] || null,
    [recordIdByFormId],
  );

  return {
    statusByFormId,
    recordIdByFormId,
    loading,
    error,
    refresh,
    getStatus,
    isFilled,
    getRecordId,
  };
}

export default useFormLibraryFillStatus;
